import React from 'react';
import ErrorBoundary from '../reporting_utils/ErrorBoundary';
import CallPercentages from './CallPercentages';
import ConversionBreakdownGraph from './ConversionBreakdownGraph';
import CallCenterConversionBreakdownGraph from './CallCenterConversionBreakdownGraph';
import TrafficAnalysisTable from './TrafficAnalysisTable';
import { Badge, BadgeList } from '../reporting_utils/Badge';
import CountWidget from '../reporting_utils/CountWidget';
import moment from 'moment';
import { momentDurationToTime } from '../reporting_utils/misc';
import TrafficByHour from './TrafficByHour';
import EmployeeBreakdownTable from './EmployeeBreakdownTable';
import LocationBreakdownTable from './LocationBreakdownTable';
import { Common } from '../../utils/common';

/**
 * @param {number | null} duration
 */
function formatDuration(duration) {
  if (duration === null) {
    duration = 0;
  }
  const momentDuration = moment.duration(duration, 'minutes').clone();
  return momentDurationToTime(momentDuration);
}

export default function ScheduledTrafficAnalysis({
  reportData: data,
  domainSettings,
  locationNames,
  groupNames,
}) {
  const { isAutomotiveIndustry, industryType } = domainSettings;

  const totalCallData = {
    inboundCalls: data.connected_call_count[0].countofinbound_connectedcalls,
    outboundCalls: data.connected_call_count[0].countofoutbound_connectedcalls,
    missedCalls: data.missed_call_count[0].missed_call_count,
  };

  const durationData = {
    totalDuration: data.duration[0].total_duration,
    avgDuration: data.duration[0].avg_minute_duration,
  };

  const employeeData = {
    appointmentsSet: data.sales_appt_count[0].countofsalesapt,
    appointmentsOffered: data.sales_appt_offered_count[0].apptoffered,
    alerts: data.alert_count[0].countofalert,
    outboundCalls: data.outbound_call_count[0].outboundcountofcalls,
  };

  const prospectData = {
    sales: data.prospect_breakdown_count[0].countof_sales_calls,
    service: data.prospect_breakdown_count[0].countof_service_calls,
    parts: data.prospect_breakdown_count[0].countof_parts_calls,
    oneTimeService: data.prospect_breakdown_count[0].countof_one_time_service_calls,
    recurringService: data.prospect_breakdown_count[0].countof_recurring_service_calls,
    prospect: data.prospect_breakdown_count[0].countof_prospect_calls,
    customerService: data.prospect_breakdown_count[0].countof_customer_service_calls,
    outboundNotConnected: data.prospect_breakdown_count[0].countof_outbound_not_connected_calls,
    currentPatient: data.prospect_breakdown_count[0].countof_current_patient_calls,
  };

  const websiteActivity = {
    totalVisitors: data.visitor_count[0].visitor_count,
    totalChats: data.chat_count[0].countofchats,
    totalLeads: data.lead_count[0].countofleads,
  };

  let prospectCallTypes = [
    { label: 'Sales', value: prospectData.sales },
    { label: 'Prospect', value: prospectData.prospect },
  ];
  switch (industryType) {
    case 'Automotive':
      prospectCallTypes = [
        { label: 'Sales', value: prospectData.sales },
        { label: 'Service', value: prospectData.service },
        { label: 'Parts', value: prospectData.parts },
      ];
      break;
    case 'Call Center':
      prospectCallTypes = [
        { label: 'Service', value: prospectData.service },
        { label: 'Parts', value: prospectData.parts },
      ];
      break;
    case 'Pest':
      prospectCallTypes = [
        { label: 'One-Time Service', value: prospectData.oneTimeService },
        { label: 'Recurring Service', value: prospectData.recurringService },
      ];
      break;
    case 'Home Improvement':
      prospectCallTypes = [
        { label: 'Sales', value: prospectData.sales },
        { label: 'Service', value: prospectData.service },
      ];
      break;
    case 'Healthcare':
      prospectCallTypes = [
        { label: 'Prospect', value: prospectData.prospect },
        { label: 'Current Patient', value: prospectData.currentPatient },
      ];
      break;
    case 'Other':
      prospectCallTypes = [
        { label: 'Sales', value: prospectData.sales },
        { label: 'Prospect', value: prospectData.prospect },
      ];
      break;
  }

  let widgetCount = 3;
  if (isAutomotiveIndustry || industryType !== 'Call Center') {
    widgetCount++;
  }

  let colClass = '';
  if (widgetCount === 3) {
    colClass = 'col-lg-4';
  } else if (widgetCount === 4) {
    colClass = 'col-sm-6';
  }

  return (
    <div className='report-details-container'>
      <div className='report-details-header'>
        <div className='report-details-title'>
          <h1>Traffic Analysis</h1>
        </div>
      </div>
      <div>
        {groupNames.length > 0 ? (
          <BadgeList title='Selected groups' maxLength={3}>
            {groupNames.map((name) => (
              <Badge label={name} key={name} />
            ))}
          </BadgeList>
        ) : null}
        {locationNames.length > 0 ? (
          <BadgeList title='Selected locations' maxLength={3}>
            {locationNames.map((name) => (
              <Badge label={name} key={name} />
            ))}
          </BadgeList>
        ) : null}
      </div>
      <ErrorBoundary>
        <div className='row' style={{ marginBottom: '15px' }}>
          <div className={colClass}>
            <CountWidget
              title='Total Calls'
              href='filtered_call_details/all/all/all/all'
              color='call'
              data={[
                {
                  label: 'Inbound Calls',
                  value: Common.formatNumberWithCommas(totalCallData.inboundCalls),
                },
                {
                  label: 'Outbound Calls',
                  value: Common.formatNumberWithCommas(totalCallData.outboundCalls),
                },
                {
                  label: 'Missed Calls',
                  value: Common.formatNumberWithCommas(totalCallData.missedCalls),
                },
              ]}
            />
          </div>
          <div className={colClass}>
            <CountWidget
              title='Call Duration'
              color='visitors'
              data={[
                {
                  label: 'Average Call Duration',
                  value: formatDuration(durationData.avgDuration),
                },
                {
                  label: 'Total Call Duration',
                  value: formatDuration(durationData.totalDuration),
                },
              ]}
            />
          </div>
          <div className={colClass}>
            <CountWidget
              title='Total Prospects'
              color='prospects'
              href='filtered_call_details/all/prospects/all/all'
              data={prospectCallTypes}
            />
          </div>
          {isAutomotiveIndustry ? (
            <div className={colClass}>
              <CountWidget
                title='Employee Performance'
                color='appointments'
                data={[
                  {
                    label: 'Appointments Offered',
                    value: Common.formatNumberWithCommas(employeeData.appointmentsOffered),
                  },
                  {
                    label: 'Appointments Set',
                    value: Common.formatNumberWithCommas(employeeData.appointmentsSet),
                  },
                  {
                    label: 'Alerts Sent',
                    value: Common.formatNumberWithCommas(employeeData.alerts),
                  },
                ]}
              />
            </div>
          ) : industryType !== 'Call Center' ? (
            <div className={colClass}>
              <CountWidget
                title='Website Activity'
                color='lead'
                data={[
                  {
                    label: 'Total Visitors',
                    value: Common.formatNumberWithCommas(websiteActivity.totalVisitors),
                  },
                  {
                    label: 'Total Chats',
                    value: Common.formatNumberWithCommas(websiteActivity.totalChats),
                  },
                  {
                    label: 'Total Forms',
                    value: Common.formatNumberWithCommas(websiteActivity.totalLeads),
                  },
                ]}
              />
            </div>
          ) : null}
        </div>
        {isAutomotiveIndustry ? <CallPercentages chartData={data.call_percentage} /> : null}
        {industryType !== 'Call Center' ? (
          <ConversionBreakdownGraph
            isAutomotiveIndustry={isAutomotiveIndustry}
            chartData={data.conversion_data}
          />
        ) : (
          <CallCenterConversionBreakdownGraph
            isAutomotiveIndustry={isAutomotiveIndustry}
            chartData={data.conversion_data}
          />
        )}
        <TrafficByHour chartData={data.traffic_by_hour} />
        <TrafficAnalysisTable
          isAutomotiveIndustry={isAutomotiveIndustry}
          tableData={data.conversion_breakdown}
          industryType={industryType}
        />
        <EmployeeBreakdownTable data={data.employee_traffic} />
        <LocationBreakdownTable data={data.location_traffic} industryType={industryType} />
      </ErrorBoundary>
    </div>
  );
}
